import { NgStyle } from '@angular/common';
import { Component, computed, input, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { MatTooltip } from '@angular/material/tooltip';
import { dsEnums } from '@vectronic/openApi-admin';
import { utcMoment } from '@vectronic/util';
import { combineLatest } from 'rxjs';

@Component({
  selector: 'rc-camera-preview',
  templateUrl: './camera-preview.component.html',
  styleUrls: ['./camera-preview.component.scss'],
  imports: [NgStyle, MatTooltip],
})
export class CameraPreviewComponent {
  ruleIndex = input<number>();
  timeInterval = input<number>();
  onTime = input<number>();
  offset = input<number>();
  cycleTime = input<number>();
  startDate = input<Date>();
  pictureInterval = input<number>();
  photoCount = input<number>();
  videoLength = input<number>();
  mode = input<dsEnums.CameraScheduleRuleType>();
  trigger = input<dsEnums.CameraScheduleRuleMainTrigger>();

  isPreviwable = computed(() => {
    return this.cycleTime() && this.onTime() && this.trigger() && this.startDate();
  });
  fixes = signal<number[]>([]);
  titles = signal<string[]>([]);
  offsetSegments = signal<{ left: number; width: number }[]>([]);

  constructor() {
    combineLatest([
      toObservable(this.trigger),
      toObservable(this.timeInterval),
      toObservable(this.offset),
      toObservable(this.onTime),
      toObservable(this.cycleTime),
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(([trigger, timeInterval, offset, duration, cycleTime]) => {
        this.fixes.update(() => {
          const fixes = [];
          if (trigger === 'TIMER' && timeInterval && cycleTime) {
            let fix = offset;
            const end = offset + duration / 60;
            while (fix < end) {
              fixes.push(fix % (cycleTime / 60));
              fix += timeInterval;
            }
          }
          return fixes;
        });
        this.updateOffsetSegments(offset, duration, cycleTime);
      });

    combineLatest([toObservable(this.cycleTime), toObservable(this.startDate)])
      .pipe(takeUntilDestroyed())
      .subscribe(([cycleTime, startDate]) => {
        if (cycleTime === 10080 * 60) {
          if (!startDate) return; // cannot compute without start date for weekly
          const startDay = utcMoment(startDate).day();
          const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
          this.titles.set(
            weekdays.map((v, i) => {
              const index = (startDay + i + 6) % 7;
              return weekdays[index];
            }),
          );
        } else if (cycleTime != null) {
          const length = Math.round(cycleTime / 3600);
          const skipped = Math.floor(length / 24);
          if (skipped > 1) {
            this.titles.set(Array.from({ length: 24 }, (v, i) => (i * skipped).toString()));
          } else {
            this.titles.set(Array.from({ length: length }, (v, i) => i.toString()));
          }
        }
      });
  }

  private updateOffsetSegments(offset: number, onTime: number, cycleTime: number) {
    if (!cycleTime) {
      this.offsetSegments.set([]);
      return;
    }
    const period = cycleTime / 60;
    const end = offset + onTime / 60;
    if (end > period) {
      const wrappedEnd = end - period;
      this.offsetSegments.set([{ left: wrappedEnd, width: Math.max(0, offset - wrappedEnd) }]);
    } else {
      this.offsetSegments.set([
        { left: 0, width: Math.max(0, offset) },
        { left: end, width: Math.max(0, period - end) },
      ]);
    }
  }

  getPercent(fix: number): number {
    return (fix * 100 * 60) / this.cycleTime();
  }

  getDateString(value: number): string {
    if (value === 0) return '00:00';
    const minutes = value % 60;
    const hours = (value - minutes) / 60;
    const hourString = hours < 10 ? `0${hours}` : hours;
    const minuteString = minutes < 10 ? `0${minutes}` : minutes;
    return `${hourString}:${minuteString}`;
  }

  getGeneralString(fix: number): string {
    let string;
    if (this.cycleTime() === 10080 * 60) {
      string = utcMoment(this.startDate())
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
        .add({ minute: fix })
        .format('ddd HH:mm');
    } else {
      string = this.getDateString(fix);
    }
    return `${string} - ${this.getTooltip()}`;
  }

  getTooltip = computed(() => {
    if (this.mode() === 'VIDEO') {
      return `Video ${this.videoLength()}s`;
    }
    if (!this.pictureInterval()) {
      return `${this.photoCount()} Photos`;
    }
    return `${this.photoCount()} Photos in ${this.pictureInterval()}s steps`;
  });
}
